import api from '../lib/api';

export interface Schedule {
  id: string;
  name: string;
  description?: string;
  organizationId: string;
  startDate: string;
  endDate: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  publishedAt?: string;
  publishedBy?: string;
  version?: number;
  parentScheduleId?: string;
  isTemplate?: boolean;
  templateName?: string;
  createdAt: string;
  updatedAt: string;
  shifts?: Shift[];
}

export interface BreakTime {
  startTime: string;
  endTime: string;
  type: 'lunch' | 'break';
}

export interface Shift {
  id: string;
  scheduleId: string;
  agentId: string;
  startTime: string;
  endTime: string;
  shiftType?: 'WORK' | 'BREAK' | 'LUNCH' | 'TRAINING' | 'PROJECT' | 'PTO' | 'SICK';
  status?: 'scheduled' | 'in_progress' | 'completed' | 'missed' | 'swap_requested' | 'swapped';
  hasLunch?: boolean;
  lunchDuration?: number;
  lunchStartTime?: string;
  lunchEndTime?: string;
  breaks?: number;
  breakDuration?: number;
  breakTimes?: Array<{ startTime: string; endTime: string }>;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  agent?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface CreateScheduleDto {
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  status?: 'DRAFT' | 'PUBLISHED';
  organizationId?: string;
}

export interface UpdateScheduleDto {
  name?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
}

export interface CreateShiftDto {
  scheduleId: string;
  agentId: string;
  startTime: string;
  endTime: string;
  shiftType?: 'WORK' | 'BREAK' | 'LUNCH' | 'TRAINING' | 'PROJECT' | 'PTO' | 'SICK';
  status?: 'scheduled' | 'in_progress' | 'completed' | 'missed' | 'swap_requested' | 'swapped';
  hasLunch?: boolean;
  lunchDuration?: number;
  lunchStartTime?: string | null;
  lunchEndTime?: string | null;
  breaks?: number;
  breakDuration?: number;
  breakTimes?: Array<{ startTime: string; endTime: string }>;
  notes?: string;
}

export interface UpdateShiftDto {
  agentId?: string;
  startTime?: string;
  endTime?: string;
  shiftType?: 'WORK' | 'BREAK' | 'LUNCH' | 'TRAINING' | 'PROJECT' | 'PTO' | 'SICK';
  status?: 'scheduled' | 'in_progress' | 'completed' | 'missed' | 'swap_requested' | 'swapped';
  breakTimes?: BreakTime[];
  notes?: string;
}

export interface ShiftSwapRequest {
  id: string;
  requestingShiftId: string;
  requestingAgentId: string;
  targetShiftId?: string;
  targetAgentId?: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  reason?: string;
  rejectionReason?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
  requestingShift?: Shift;
  targetShift?: Shift;
  requestingAgent?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  targetAgent?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export interface CreateSwapRequestDto {
  requestingShiftId: string;
  targetShiftId?: string;
  targetAgentId?: string;
  reason?: string;
}

export interface ReviewSwapRequestDto {
  status: 'approved' | 'rejected';
  rejectionReason?: string;
}

export interface ScheduleStats {
  totalShifts: number;
  scheduledShifts: number;
  completedShifts: number;
  missedShifts: number;
  inProgressShifts: number;
  completionRate: number;
}

export const schedulesService = {
  // Schedule operations
  getAll: async (organizationId?: string): Promise<Schedule[]> => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get('/schedules', { params });
    return response.data;
  },

  getById: async (id: string): Promise<Schedule> => {
    const response = await api.get(`/schedules/${id}`);
    return response.data;
  },

  create: async (schedule: CreateScheduleDto): Promise<Schedule> => {
    const response = await api.post('/schedules', schedule);
    return response.data;
  },

  update: async (id: string, schedule: UpdateScheduleDto): Promise<Schedule> => {
    const response = await api.patch(`/schedules/${id}`, schedule);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/schedules/${id}`);
  },

  publish: async (id: string): Promise<Schedule> => {
    const response = await api.post(`/schedules/${id}/publish`);
    return response.data;
  },

  archive: async (id: string): Promise<Schedule> => {
    const response = await api.post(`/schedules/${id}/archive`);
    return response.data;
  },

  getStats: async (id: string): Promise<ScheduleStats> => {
    const response = await api.get(`/schedules/${id}/stats`);
    return response.data;
  },

  // Versioning and templates
  duplicate: async (id: string): Promise<Schedule> => {
    const response = await api.post(`/schedules/${id}/duplicate`);
    return response.data;
  },

  saveAsTemplate: async (id: string, templateName: string): Promise<Schedule> => {
    const response = await api.post(`/schedules/${id}/save-as-template`, { templateName });
    return response.data;
  },

  getTemplates: async (organizationId?: string): Promise<Schedule[]> => {
    const params = organizationId ? { organizationId } : {};
    const response = await api.get('/schedules/templates/list', { params });
    return response.data;
  },

  createFromTemplate: async (templateId: string, name: string, startDate: string, endDate: string): Promise<Schedule> => {
    const response = await api.post(`/schedules/templates/${templateId}/create-from`, {
      name,
      startDate,
      endDate,
    });
    return response.data;
  },

  // Shift operations
  getShifts: async (scheduleId?: string, agentId?: string): Promise<Shift[]> => {
    const params: any = {};
    if (scheduleId) params.scheduleId = scheduleId;
    if (agentId) params.agentId = agentId;
    const response = await api.get('/schedules/shifts', { params });
    return response.data;
  },

  getShift: async (id: string): Promise<Shift> => {
    const response = await api.get(`/schedules/shifts/${id}`);
    return response.data;
  },

  createShift: async (shift: CreateShiftDto): Promise<Shift> => {
    const response = await api.post('/schedules/shifts', shift);
    return response.data;
  },

  updateShift: async (id: string, shift: UpdateShiftDto): Promise<Shift> => {
    const response = await api.patch(`/schedules/shifts/${id}`, shift);
    return response.data;
  },

  deleteShift: async (id: string): Promise<void> => {
    await api.delete(`/schedules/shifts/${id}`);
  },

  // Bulk shift operations
  createBulkShifts: async (shifts: CreateShiftDto[]): Promise<Shift[]> => {
    const promises = shifts.map(shift => api.post('/schedules/shifts', shift));
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  },

  updateBulkShifts: async (updates: { id: string; data: UpdateShiftDto }[]): Promise<Shift[]> => {
    const promises = updates.map(({ id, data }) => api.patch(`/schedules/shifts/${id}`, data));
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  },

  deleteBulkShifts: async (ids: string[]): Promise<void> => {
    await Promise.all(ids.map(id => api.delete(`/schedules/shifts/${id}`)));
  },

  // Shift swap operations
  createSwapRequest: async (dto: CreateSwapRequestDto): Promise<ShiftSwapRequest> => {
    const response = await api.post('/schedules/swap-requests', dto);
    return response.data;
  },

  getSwapRequests: async (organizationId?: string, status?: string): Promise<ShiftSwapRequest[]> => {
    const params: any = {};
    if (organizationId) params.organizationId = organizationId;
    if (status) params.status = status;
    const response = await api.get('/schedules/swap-requests', { params });
    return response.data;
  },

  getSwapRequest: async (id: string): Promise<ShiftSwapRequest> => {
    const response = await api.get(`/schedules/swap-requests/${id}`);
    return response.data;
  },

  reviewSwapRequest: async (id: string, dto: ReviewSwapRequestDto): Promise<ShiftSwapRequest> => {
    const response = await api.post(`/schedules/swap-requests/${id}/review`, dto);
    return response.data;
  },

  cancelSwapRequest: async (id: string): Promise<ShiftSwapRequest> => {
    const response = await api.post(`/schedules/swap-requests/${id}/cancel`);
    return response.data;
  },

  // Coverage analytics
  getCoverageAnalytics: async (scheduleId: string): Promise<any> => {
    const response = await api.get(`/schedules/${scheduleId}/coverage-analytics`);
    return response.data;
  },

  // Auto-scheduling
  autoSchedule: async (scheduleId: string, params: {
    startDate: string;
    endDate: string;
    shiftsPerDay?: number;
    shiftDurationHours?: number;
    targetCoverage?: number;
  }): Promise<Shift[]> => {
    const response = await api.post(`/schedules/${scheduleId}/auto-schedule`, params);
    return response.data;
  },
};
